import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { LuUpload, LuX, LuFile, LuCheck } from 'react-icons/lu';
import './DragNDropFiles.css';

import ValidationAlert from '../../../app/components/ValidationAlert';
import { useTranslation } from '../../hooks/useTranslation';

export type DragNDropFile = {
  id: string;
  name: string;
  size: number;
};

export type NewFile = {
  id: string;
  name: string;
  content: Buffer;
};

interface DragNDropFilesProps {
  existingFiles?: DragNDropFile[];
  setFilesToAdd: React.Dispatch<React.SetStateAction<NewFile[]>>;
  setFilesToDelete?: React.Dispatch<React.SetStateAction<string[]>>;
  setMaxFileSizeExceeded?: React.Dispatch<React.SetStateAction<boolean>>;
  maxFileSize?: number;
}

const DragNDropFiles: React.FC<DragNDropFilesProps> = props => {
  const maxFileSize = props.maxFileSize || 25 * 1024 * 1024;
  const [files, setFiles] = useState<DragNDropFile[]>(
    props.existingFiles || [],
  );
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const { t, tk } = useTranslation('commonLng');

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const newFiles = acceptedFiles.map(file => ({
        id: Math.random().toString(36).substr(2, 9),
        name: file.name,
        size: file.size,
      }));
      setFiles(prev => [...prev, ...newFiles]);
      acceptedFiles.forEach(fileObj => {
        const reader = new FileReader();
        reader.onload = () => {
          const fileContent = reader.result;
          const attachment: NewFile = {
            id: newFiles.find(f => f.name === fileObj.name)?.id || '',
            name: fileObj.name,
            content: Buffer.from(fileContent as ArrayBuffer),
          };
          props.setFilesToAdd(prev => [...prev, attachment]);
        };
        reader.readAsArrayBuffer(fileObj);
      });
    },
    [props],
  );

  useEffect(() => {
    const totalSize = files.reduce((acc, file) => acc + file.size, 0);
    if (totalSize > maxFileSize) {
      setShowSaveErrors(true);
      setSaveErrors([
        t(tk.attachment.fileSizeExceededWithSize, {
          maxSize: formatFileSize(maxFileSize),
        }),
      ]);
      props.setMaxFileSizeExceeded?.(true);
    } else {
      setShowSaveErrors(false);
      setSaveErrors([]);
      props.setMaxFileSizeExceeded?.(false);
    }
  }, [files, maxFileSize, props, t, tk.attachment.fileSizeExceededWithSize]);

  useEffect(() => {
    setFiles(props.existingFiles || []);
  }, [props.existingFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    accept: {
      'application/pdf': [],
      'application/msword': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        [],
      //'text/plain': [],
      'image/jpeg': [],
      'image/png': [],
      'image/gif': [],
      'application/vnd.ms-excel': [],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
    },
  });

  const removeFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
    if (props.existingFiles?.find(f => f.id === fileId)) {
      props.setFilesToDelete?.(prev => [...(prev || []), fileId]);
    } else {
      props.setFilesToAdd(prev => prev.filter(f => f.id !== fileId));
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  return (
    <div className="file-upload-container">
      <div
        {...getRootProps()}
        className={`dropzone ${isDragActive ? 'dropzone-active' : ''}`}
      >
        <input {...getInputProps()} />

        <div className="dropzone-content">
          <LuUpload
            className={`upload-icon ${isDragActive ? 'text-blue-500' : 'text-gray-400'}`}
          />
          <div>
            <p className="dropzone-text">
              {isDragActive
                ? t(tk.attachment.dragAndDropFiles)
                : t(tk.attachment.clickToSelectFiles)}
            </p>
            <p className="dropzone-subtext">
              {t(tk.attachment.supportedFormats)}: PDF, DOC, DOCX, JPG, PNG,
              GIF, XLS, XLSX
            </p>
          </div>
        </div>
      </div>

      {files.length > 0 && (
        <div className="file-list">
          <h3 className="file-list-title">
            {t(tk.attachment.files)} ({files.length})
          </h3>
          {files.map(fileObj => (
            <div key={fileObj.id} className="file-item">
              <div className="file-details">
                <LuFile className="file-icon" />
                <div className="file-meta">
                  <span className="file-name">{fileObj.name}</span>
                  <span className="file-size">
                    {formatFileSize(fileObj.size)}
                  </span>
                </div>
              </div>
              <div className="file-actions">
                <LuCheck className="file-check-icon" />
                <button
                  onClick={() => removeFile(fileObj.id)}
                  className="file-remove-button"
                >
                  <LuX />
                </button>
              </div>
            </div>
          ))}
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="m-3"
          />
        </div>
      )}
    </div>
  );
};

export default DragNDropFiles;
